import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import {
  BaseAdminScreen,
  ResidentDetailsHeader,
  ResidentDetailsInfo,
  ResidentEditForm,
  AdminErrorBoundary,
} from '@/components';
import {
  KPISummaryBar,
  TabSegment,
  type StatTileSpec,
  type TabSegmentItem,
} from '@/components/shared/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { useResidentDetails } from '@/hooks/admin/useResidentDetails';
import { RESIDENT_DETAILS_TABS } from '@/constants/admin/residents';
import { formatDateShort } from '@/utils/admin/date';
import { TransactionsBody } from '@/components/admin/resident/common/TransactionsBody';
import { useResidentTransactions } from '@/hooks/admin/useResidentTransactions';
import { ResidentDetailsSkeleton } from './ResidentDetailsSkeleton';
import { TransactionHistorySkeleton } from '@/components/common/skeleton';
import { useResponsive } from '@/hooks/common/useResponsive';

type ResidentDetailsProps = StackScreenProps<
  AdminStackParamList,
  'ResidentDetails'
>;

type ResidentTabKey =
  | typeof RESIDENT_DETAILS_TABS.GENERAL
  | 'Transactions'
  | typeof RESIDENT_DETAILS_TABS.EDIT;

export const ResidentDetails: React.FC<ResidentDetailsProps> = ({
  navigation,
  route,
}) => {
  const { accountId, leaseId, mode } = route.params;
  const { showApiError } = useErrorToast();
  const styles = useStyles();
  const { showFullTable } = useResponsive();
  const insets = useSafeAreaInsets();
  // Reserve room above the iOS home indicator and the Android nav/gesture bar.
  // Fall back to a platform-appropriate minimum so devices that report 0 still
  // get breathing room. Applied as an explicit spacer at the end of the scroll
  // (a real child View is more reliable than contentContainerStyle padding,
  // which some keyboard wrappers override on Android).
  const bottomInset = Math.max(
    insets.bottom,
    Platform.OS === 'ios' ? 16 : 24,
  );

  // Manual keyboard avoidance: measure the wrapper's actual screen position to
  // compute exact wrapper/keyboard overlap, then render a sibling spacer that
  // shrinks the ScrollView frame from the bottom. Avoids the double-padding
  // that KeyboardAvoidingView + automaticallyAdjustKeyboardInsets produced.
  const wrapperRef = useRef<View>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const isAndroid = Platform.OS === 'android';
    const showEvent = isAndroid ? 'keyboardDidShow' : 'keyboardWillShow';
    const hideEvent = isAndroid ? 'keyboardDidHide' : 'keyboardWillHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      const keyboardTop = e.endCoordinates.screenY;
      if (wrapperRef.current) {
        wrapperRef.current.measureInWindow((_x, y, _w, h) => {
          setKeyboardHeight(Math.max(0, y + h - keyboardTop));
        });
      } else {
        setKeyboardHeight(e.endCoordinates.height);
      }
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const {
    residentDetails,
    isLoading,
    error,
    enhancedError,
    refetch,
    retryCount,
    canRetry,
  } = useResidentDetails({ accountId, leaseId, mode: mode || 'resident' });

  const { transactions: txList, isLoading: txLoading } = useResidentTransactions({
    accountId,
    leaseId,
    autoFetch: true,
  });

  useErrorToastOnce(enhancedError, showApiError);

  const tabs: ResidentTabKey[] = [
    RESIDENT_DETAILS_TABS.GENERAL,
    'Transactions',
    RESIDENT_DETAILS_TABS.EDIT,
  ];
  const tabLabels: Record<ResidentTabKey, string> = {
    [RESIDENT_DETAILS_TABS.GENERAL]: 'Overview',
    Transactions: 'Transactions',
    [RESIDENT_DETAILS_TABS.EDIT]: 'Edit',
  };
  const tabItems = useMemo<TabSegmentItem<ResidentTabKey>[]>(
    () => tabs.map((t) => ({ key: t, label: tabLabels[t] })),
    // tabs is a stable literal; safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [activeTab, setActiveTab] = React.useState<ResidentTabKey>(tabs[0]);

  const kpiTiles = useMemo<StatTileSpec[]>(() => {
    if (!residentDetails) return [];
    const balance = residentDetails.balanceDue;
    const last = residentDetails.lastPaymentInfo;
    const monthsRemaining = residentDetails.endDate
      ? Math.max(
          0,
          Math.round(
            (new Date(residentDetails.endDate).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24 * 30),
          ),
        )
      : null;

    const roommates = residentDetails.roommates ?? [];
    const roommateNames = roommates
      .slice(0, 3)
      .map((r: any) => r.firstName ?? r.data?.account?.firstName)
      .filter(Boolean)
      .join(' · ');

    return [
      {
        id: 'balance',
        label: 'Balance Due',
        value: balance != null ? `$${balance.toFixed(2)}` : '—',
        trend:
          balance === 0
            ? { tone: 'success' as const, text: 'Paid' }
            : balance && balance > 0
            ? { tone: 'warn' as const, text: 'Past due' }
            : undefined,
      },
      {
        id: 'lastPayment',
        label: 'Last Payment',
        value: last ? formatDateShort(new Date(last.date)) : '—',
        caption: last
          ? `$${last.amount.toFixed(2)} · ${last.settlementStatus ?? 'cleared'}`
          : undefined,
        captionFallback: 'No payments on file',
      },
      {
        id: 'leaseEnds',
        label: 'Lease Ends',
        value: residentDetails.endDate
          ? formatDateShort(new Date(residentDetails.endDate))
          : '—',
        caption: monthsRemaining != null ? `${monthsRemaining} mo remaining` : undefined,
        captionFallback: 'Month-to-month',
      },
      {
        id: 'roommates',
        label: 'Roommates',
        value: String(roommates.length),
        caption: roommateNames || undefined,
        captionFallback: 'Living alone',
      },
    ];
  }, [residentDetails]);

  const goToTransactions = () => {
    setActiveTab('Transactions');
  };

  const renderContent = () => {
    if (isLoading && !residentDetails) {
      return <ResidentDetailsSkeleton />;
    }

    if (error && !residentDetails) {
      return (
        <AdminErrorState
          title="Unable to Load Resident Details"
          message={
            enhancedError?.userFriendlyMessage ||
            'Something went wrong while loading the resident details.'
          }
          retryCount={retryCount}
          canRetry={canRetry}
          onRetry={refetch}
        />
      );
    }

    if (!residentDetails) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.noDataText}>No resident details found</Text>
        </View>
      );
    }

    return (
      <>
        <View style={styles.headerContainer}>
          <ResidentDetailsHeader resident={residentDetails} />
        </View>

        <View style={styles.kpiContainer}>
          <KPISummaryBar tiles={kpiTiles} />
        </View>

        <View style={styles.tabContainer}>
          <TabSegment<ResidentTabKey>
            items={tabItems}
            selectedKey={activeTab}
            onChange={setActiveTab}
          />
        </View>

        {activeTab === RESIDENT_DETAILS_TABS.GENERAL ? (
          <View style={styles.tabContent}>
            <ResidentDetailsInfo
              resident={residentDetails}
              leaseCustomerId={accountId}
              leaseId={leaseId}
              onViewAllTransactions={goToTransactions}
            />
          </View>
        ) : activeTab === 'Transactions' ? (
          <View style={styles.tabContent}>
            {txLoading ? (
              showFullTable ? (
                <TransactionHistorySkeleton variant="table" count={5} showHeader />
              ) : (
                <TransactionHistorySkeleton variant="card" count={5} />
              )
            ) : (
              <TransactionsBody transactions={txList} />
            )}
          </View>
        ) : (
          <ResidentEditForm
            accountId={accountId}
            residentDetails={residentDetails}
          />
        )}
      </>
    );
  };

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="ResidentDetails"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <View ref={wrapperRef} style={styles.scroll}>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.backLinkContainer}>
              <ScreenBackLink style={styles.backLink} />
            </View>
            {renderContent()}
            <View style={{ height: bottomInset + 16 }} />
          </ScrollView>
          {keyboardHeight > 0 && <View style={{ height: keyboardHeight }} />}
        </View>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      scroll: {
        flex: 1,
      },
      scrollContent: {
        flexGrow: 1,
      },
      baseScreenContent: {
        paddingHorizontal: 0,
        paddingTop: 0,
        paddingBottom: 0,
        flexGrow: 1,
      },
      centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
      },
      loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      noDataText: {
        fontSize: 16,
        color: components.admin.dataCell.textMuted,
      },
      backLinkContainer: {
        paddingHorizontal: 16,
      },
      backLink: {
        marginTop: 8,
        marginBottom: -4,
      },
      headerContainer: {
        paddingHorizontal: 16,
        paddingTop: 12,
      },
      kpiContainer: {
        paddingHorizontal: 16,
        paddingTop: 12,
      },
      tabContainer: {
        paddingHorizontal: 16,
        paddingTop: 14,
        paddingBottom: 12,
      },
      tabContent: {
        paddingHorizontal: 16,
      },
    }),
  );
